import {useCallback, useEffect, useMemo, useState} from "react";
import {ChevronLeft, ChevronRight, Search} from "lucide-react";
import {
  deleteAdminInfluencerRate,
  getAdminInfluencerRates,
  upsertAdminInfluencerRate,
  type AdminInfluencerRatesResponse
} from "@corporatesclub/shared";
import {adminToast} from "@/lib/adminToast";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {AdminAlert, AdminBadge, AdminCard, AdminLoading, AdminPageLayout, AdminToolbar} from "./ui";

type ScopeRow = AdminInfluencerRatesResponse["scopes"][number];
type MainTab = "categories" | "brands";
type CatTrail =
  | {level: "root"}
  | {level: "category"; category: ScopeRow}
  | {level: "subcategory"; category: ScopeRow; subcategory: ScopeRow};

function scopeKey(s: Pick<ScopeRow, "type" | "id">) {
  return `${s.type}:${s.id}`;
}

export function InfluencerRatesView() {
  const [data, setData] = useState<AdminInfluencerRatesResponse | null>(null);
  const [err, setErr] = useState("");
  const [mainTab, setMainTab] = useState<MainTab>("categories");
  const [trail, setTrail] = useState<CatTrail>({level: "root"});
  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editPercent, setEditPercent] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    getAdminInfluencerRates()
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSearch("");
    setEditId(null);
    if(mainTab === "brands") setTrail({level: "root"});
  }, [mainTab]);

  const categories = useMemo(
    () => (data?.scopes ?? []).filter((s) => s.type === "category"),
    [data]
  );
  const subcategories = useMemo(
    () => (data?.scopes ?? []).filter((s) => s.type === "subcategory"),
    [data]
  );
  const subsubs = useMemo(
    () => (data?.scopes ?? []).filter((s) => s.type === "subsubcategory"),
    [data]
  );
  const brands = useMemo(
    () => (data?.scopes ?? []).filter((s) => s.type === "brand"),
    [data]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows: ScopeRow[] = [];
    if(mainTab === "brands") {
      rows = brands;
    } else if(trail.level === "root") {
      rows = categories;
    } else if(trail.level === "category") {
      rows = subcategories.filter((s) => s.parentId === trail.category.id);
    } else {
      rows = subsubs.filter((s) => s.parentId === trail.subcategory.id);
    }
    if(!q) return rows;
    return rows.filter((s) => s.label.toLowerCase().includes(q) || String(s.id).includes(q));
  }, [mainTab, trail, categories, subcategories, subsubs, brands, search]);

  function isOverride(s: ScopeRow) {
    return Boolean(data?.overrides.some((o) => o.scopeType === s.type && o.scopeId === s.id));
  }

  function canDrill(s: ScopeRow) {
    if(mainTab !== "categories") return false;
    if(s.type === "category") return subcategories.some((x) => x.parentId === s.id);
    if(s.type === "subcategory") return subsubs.some((x) => x.parentId === s.id);
    return false;
  }

  function openChild(s: ScopeRow) {
    if(s.type === "category") {
      setTrail({level: "category", category: s});
      setSearch("");
      setEditId(null);
      return;
    }
    if(s.type === "subcategory" && trail.level === "category") {
      setTrail({level: "subcategory", category: trail.category, subcategory: s});
      setSearch("");
      setEditId(null);
    }
  }

  function goRoot() {
    setTrail({level: "root"});
    setSearch("");
    setEditId(null);
  }

  function goCategory() {
    if(trail.level === "subcategory") {
      setTrail({level: "category", category: trail.category});
      setSearch("");
      setEditId(null);
    }
  }

  async function saveScope(s: ScopeRow) {
    const percent = parseFloat(editPercent || String(s.percent));
    if(Number.isNaN(percent) || percent < 0 || percent > 100) {
      adminToast.error("Enter a percent between 0 and 100.");
      return;
    }
    setSaving(true);
    try {
      const next = await upsertAdminInfluencerRate({scopeType: s.type, scopeId: s.id, percent, activ: 1});
      setData(next);
      setEditId(null);
      adminToast.success("Rate saved.");
    } catch(e: unknown) {
      adminToast.error(e instanceof Error ? e.message : "Could not save rate");
    } finally {
      setSaving(false);
    }
  }

  async function resetScope(s: ScopeRow) {
    const ov = data?.overrides.find((o) => o.scopeType === s.type && o.scopeId === s.id);
    if(!ov) return;
    setSaving(true);
    try {
      const next = await deleteAdminInfluencerRate(ov.id);
      setData(next);
      setEditId(null);
      adminToast.success("Reset to default.");
    } catch(e: unknown) {
      adminToast.error(e instanceof Error ? e.message : "Could not reset rate");
    } finally {
      setSaving(false);
    }
  }

  const listTitle =
    mainTab === "brands"
      ? "Brands"
      : trail.level === "root"
        ? "Categories"
        : trail.level === "category"
          ? `Subcategories · ${trail.category.label}`
          : `Sub-subcategories · ${trail.subcategory.label}`;

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  if(!data) return <AdminLoading lines={8} />;

  return (
    <AdminPageLayout
      title="Rates"
      description={`Rate card (default ${data.defaultPercent}%). Missing override uses the default.`}
      breadcrumbs={[
        {label: "Dashboard", href: "/vadmin"},
        {label: "Influencers"},
        {label: "Rates"}
      ]}
    >
      <AdminToolbar>
        <p className="text-sm text-muted-foreground">
          Active overrides: <strong>{data.overrides.length}</strong>
          {" · "}
          Click a category to open its subcategories, then drill into sub-subcategories.
        </p>
      </AdminToolbar>

      <div className="admin-inf-tabs" role="tablist" aria-label="Scope type">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "categories"}
          className={cn("admin-inf-tabs__btn", mainTab === "categories" && "isActive")}
          onClick={() => setMainTab("categories")}
        >
          Categories
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "brands"}
          className={cn("admin-inf-tabs__btn", mainTab === "brands" && "isActive")}
          onClick={() => setMainTab("brands")}
        >
          Brands
        </button>
      </div>

      <AdminCard
        title={listTitle}
        description={
          mainTab === "categories"
            ? "Set commission % per level. Drill down with the row arrow."
            : "Set commission % per brand."
        }
      >
        {mainTab === "categories" && trail.level !== "root" ? (
          <nav className="admin-inf-crumb" aria-label="Category path">
            <button type="button" className="admin-inf-crumb__link" onClick={goRoot}>
              <ChevronLeft className="h-3.5 w-3.5" />
              Categories
            </button>
            {trail.level === "category" || trail.level === "subcategory" ? (
              <>
                <span className="admin-inf-crumb__sep">/</span>
                {trail.level === "subcategory" ? (
                  <button type="button" className="admin-inf-crumb__link" onClick={goCategory}>
                    {trail.category.label}
                  </button>
                ) : (
                  <span className="admin-inf-crumb__current">{trail.category.label}</span>
                )}
              </>
            ) : null}
            {trail.level === "subcategory" ? (
              <>
                <span className="admin-inf-crumb__sep">/</span>
                <span className="admin-inf-crumb__current">{trail.subcategory.label}</span>
              </>
            ) : null}
          </nav>
        ) : null}

        <div className="admin-inf-search">
          <Search className="admin-inf-search__icon h-4 w-4" aria-hidden />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={mainTab === "brands" ? "Search brands..." : "Search in this level..."}
            className="pl-9"
            aria-label="Search scopes"
          />
        </div>

        {visibleRows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No items at this level.</p>
        ) : (
          <ul className="admin-inf-list">
            {visibleRows.map((s) => {
              const key = scopeKey(s);
              const override = isOverride(s);
              const drill = canDrill(s);
              const editing = editId === key;
              return (
                <li key={key} className={cn("admin-inf-row", editing && "isEditing")}>
                  <div className="admin-inf-row__main">
                    <div className="min-w-0">
                      <p className="admin-inf-row__title">{s.label}</p>
                      <p className="admin-inf-row__meta">
                        {s.type}
                        {override ? " · override" : " · default"}
                      </p>
                    </div>
                    {editing ? (
                      <div className="admin-inf-row__edit" onClick={(e) => e.stopPropagation()}>
                        <Input
                          className="h-9 w-20"
                          value={editPercent}
                          onChange={(e) => setEditPercent(e.target.value)}
                          inputMode="decimal"
                          aria-label="Percent"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                        <Button size="sm" disabled={saving} onClick={() => saveScope(s)}>Save</Button>
                        {override ? (
                          <Button size="sm" variant="secondary" disabled={saving} onClick={() => resetScope(s)}>
                            Reset
                          </Button>
                        ) : null}
                        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditId(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="admin-inf-row__actions">
                        <AdminBadge variant={override ? "default" : "outline"}>{s.percent}%</AdminBadge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditId(key);
                            setEditPercent(String(s.percent));
                          }}
                        >
                          Edit
                        </Button>
                        {drill ? (
                          <Button size="sm" variant="ghost" className="gap-1" onClick={() => openChild(s)}>
                            Open
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </AdminCard>
    </AdminPageLayout>
  );
}
